import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useCallback, useEffect } from 'react';
import { Video, Globe, ShoppingBag, Mail, Bot, ArrowRight, CheckCircle2, Zap, Sparkles, MessageSquare, TrendingUp, BrainCircuit, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';
import { Link } from 'react-router-dom';
import { trackCTAClick } from '@/lib/metaPixel';
import useEmblaCarousel from 'embla-carousel-react';
import { useIsMobile } from '@/hooks/use-mobile';
import auraMascot from '@/assets/aura-mascot.png';

const projects = [
  {
    icon: Video,
    title: "Faceless YouTube Channels",
    description: "Create automated YouTube channels using AI scripts and simple production workflows.",
    outcome: "Monetization: Ad revenue & affiliate income",
    color: "blue",
  },
  {
    icon: TrendingUp,
    title: "Viral Content Pages",
    description: "Build TikTok and Instagram pages that grow audiences without showing your face.",
    outcome: "Monetization: Grow audiences and attract brand deals",
    color: "purple",
  },
  {
    icon: Globe,
    title: "Traffic Websites",
    description: "Launch niche websites that attract organic traffic and monetize with ads and affiliate offers.",
    outcome: "Monetization: Attract free traffic from Google",
    color: "green",
  },
  {
    icon: ShoppingBag,
    title: "AI-Powered Stores",
    description: "Build simple ecommerce stores using AI product pages and automated marketing.",
    outcome: "Monetization: Launch products without holding inventory",
    color: "orange",
  },
  {
    icon: Mail,
    title: "Newsletter Businesses",
    description: "Create niche newsletters and grow an audience you can monetize with sponsors and digital products.",
    outcome: "Monetization: Build audiences you can monetize anytime",
    color: "pink",
  },
  {
    icon: Bot,
    title: "Automation Systems",
    description: "Build AI workflows that generate content, manage tasks, and scale online projects.",
    outcome: "Monetization: Automate work with AI systems",
    color: "slate",
  },
];

const colorClasses = {
  blue: { bg: "bg-blue-500/10 dark:bg-blue-500/15", border: "border-blue-500/20", icon: "text-blue-600 dark:text-blue-400", outcome: "text-blue-600/70 dark:text-blue-400/70" },
  purple: { bg: "bg-purple-500/10 dark:bg-purple-500/15", border: "border-purple-500/20", icon: "text-purple-600 dark:text-purple-400", outcome: "text-purple-600/70 dark:text-purple-400/70" },
  green: { bg: "bg-green-500/10 dark:bg-green-500/15", border: "border-green-500/20", icon: "text-green-600 dark:text-green-400", outcome: "text-green-600/70 dark:text-green-400/70" },
  orange: { bg: "bg-orange-500/10 dark:bg-orange-500/15", border: "border-orange-500/20", icon: "text-orange-600 dark:text-orange-400", outcome: "text-orange-600/70 dark:text-orange-400/70" },
  pink: { bg: "bg-pink-500/10 dark:bg-pink-500/15", border: "border-pink-500/20", icon: "text-pink-600 dark:text-pink-400", outcome: "text-pink-600/70 dark:text-pink-400/70" },
  slate: { bg: "bg-muted", border: "border-border", icon: "text-muted-foreground", outcome: "text-muted-foreground/70" },
};

const week1Items = [
  "Choose your niche",
  "Set up your content structure",
  "Generate your first 10 pieces",
  "Understand your monetization path",
];

const insideYouGet = [
  "🤖 AI Personal Teacher — 24/7",
  "5 structured business models",
  "50 implementation modules",
  "500+ short, actionable lessons",
  "🖼️ AI Image Creator",
  "🎬 AI Video Script Generator",
  "✍️ AI Caption & Hook Writer",
  "🔍 AI Niche Analyzer",
  "📅 AI Content Strategy Planner",
  "Real workflow demonstrations",
  "Private community access",
  "Continuous updates",
];

// Group items for carousel slides (4 per slide)
const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const InsideYouGetCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const slides = chunkArray(insideYouGet, 4);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {slides.map((slide, slideIdx) => (
            <div key={slideIdx} className="flex-[0_0_100%] min-w-0 px-1">
              <div className="space-y-2.5">
                {slide.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-card border border-border">
                    <Zap className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground">
                      <TranslatedText>{item}</TranslatedText>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {scrollSnaps.map((_, idx) => (
          <button
            key={idx}
            onClick={() => emblaApi?.scrollTo(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === selectedIndex ? 'bg-primary w-6' : 'bg-border'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const InsideYouGetDesktop = () => (
  <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto mt-6">
    {insideYouGet.map((item, i) => (
      <div key={i} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border">
        <Zap className="w-4 h-4 text-primary flex-shrink-0" />
        <span className="text-sm font-medium text-foreground">
          <TranslatedText>{item}</TranslatedText>
        </span>
      </div>
    ))}
  </div>
);

export const LightSolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const isMobile = useIsMobile();

  return (
    <section ref={ref} className="py-12 sm:py-20 px-4 bg-gradient-to-b from-muted/50 to-background">
      <div className="max-w-6xl mx-auto">
        {/* What Happens After You Join — Week 1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto mb-16"
        >
          <div className="text-center mb-6">
            <span className="text-sm font-medium text-muted-foreground tracking-widest uppercase mb-4 block">
              <TranslatedText>Week 1</TranslatedText>
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              <TranslatedText>What Happens After You Join?</TranslatedText>
            </h2>
          </div>

          <div className="p-5 sm:p-6 rounded-2xl bg-card border border-border">
            <ul className="space-y-3 mb-4">
              {week1Items.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-sm sm:text-base text-foreground">
                    <TranslatedText>{item}</TranslatedText>
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm font-semibold text-primary">
              <TranslatedText>No theory. Just implementation.</TranslatedText>
            </p>
          </div>
        </motion.div>

        {/* Inside You Get */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 px-2">
            <TranslatedText>Inside You Get</TranslatedText>
          </h2>

          {isMobile ? (
            <div className="mt-5 max-w-sm mx-auto">
              <InsideYouGetCarousel />
            </div>
          ) : (
            <InsideYouGetDesktop />
          )}
        </motion.div>

        {/* AI Personal Teacher — High-Impact Feature */}
        <div id="ai-teacher" className="scroll-mt-20" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-20 mb-16"
        >
          {/* Section intro */}
          <div className="text-center mb-8">
            <motion.span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-600 text-xs font-bold uppercase tracking-wider border border-green-500/20 mb-4"
              animate={isInView ? { opacity: [1, 0.7, 1] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Live & Available 24/7
            </motion.span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 leading-tight">
              <TranslatedText>Meet Your AI Personal Teacher</TranslatedText>
            </h2>
            <p className="text-base text-muted-foreground max-w-lg mx-auto">
              <TranslatedText>Not a chatbot. A real AI mentor that knows every lesson, guides your projects, and answers in seconds.</TranslatedText>
            </p>
          </div>

          <div className="relative overflow-visible rounded-2xl border border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
            {/* Animated gradient background */}
            <motion.div
              className="absolute -top-16 -right-16 w-56 h-56 bg-primary/8 rounded-full blur-3xl"
              animate={isInView ? { scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] } : {}}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
              className="absolute -bottom-12 -left-12 w-44 h-44 bg-accent/8 rounded-full blur-3xl"
              animate={isInView ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />

            <div className="relative z-10 grid sm:grid-cols-2 gap-6 sm:gap-8 items-center p-5 sm:p-8 lg:p-10">
              {/* Left: Content */}
              <div>
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider">
                    <Sparkles className="w-3 h-3" />
                    <TranslatedText>Included Free</TranslatedText>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    <TrendingUp className="w-3 h-3" />
                    +350%
                  </span>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 leading-tight">
                  <TranslatedText>Like Having a Private Mentor — But Available Anytime</TranslatedText>
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-5">
                  <TranslatedText>Aura AI knows your entire course material. Ask a question, get a real answer — not generic advice. It guides you through building your projects step by step.</TranslatedText>
                </p>

                <ul className="space-y-3 mb-6">
                  {[
                    { icon: MessageSquare, text: "Ask anything — instant, clear answers about your lessons" },
                    { icon: BrainCircuit, text: "Step-by-step guidance for building real projects" },
                    { icon: TrendingUp, text: "Members report 350% faster implementation speed" },
                    { icon: Sparkles, text: "Unlimited & free with every plan — no extra cost" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-sm text-foreground leading-relaxed">
                        <TranslatedText>{item.text}</TranslatedText>
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to="/apply?plan=free" onClick={() => trackCTAClick('Try AI Teacher Free', 'AI Teacher')}>
                  <Button className="bg-foreground hover:bg-foreground/90 text-background font-semibold h-12 px-8 rounded-xl shadow-lg w-full sm:w-auto">
                    <TranslatedText>Try It Free — Ask Your First Question</TranslatedText>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-[11px] text-muted-foreground mt-2">
                  <TranslatedText>No credit card required · Instant access</TranslatedText>
                </p>
              </div>

              {/* Right: Chat mockup with mascot peeking */}
              <div className="order-first sm:order-last relative">
                {/* Mascot peeking from behind the chat box */}
                <motion.div
                  className="absolute -top-10 -right-4 sm:-top-12 sm:-right-6 z-20"
                  initial={{ y: 30, opacity: 0, rotate: 5 }}
                  animate={isInView ? { y: 0, opacity: 1, rotate: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5, type: "spring", stiffness: 100 }}
                >
                  <motion.div
                    animate={isInView ? { y: [0, -6, 0] } : {}}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {/* Glow */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-gradient-to-t from-primary/30 to-accent/30 blur-xl scale-125"
                      animate={isInView ? { scale: [1.25, 1.4, 1.25], opacity: [0.4, 0.7, 0.4] } : {}}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <img
                      src={auraMascot}
                      alt="Aura AI Teacher"
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-3 border-background shadow-2xl relative z-10"
                    />
                    {/* Online indicator */}
                    <motion.div
                      className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-green-500 rounded-full border-[3px] border-background z-20 flex items-center justify-center"
                      animate={isInView ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Sparkles className="w-2.5 h-2.5 text-white" />
                    </motion.div>
                    {/* Floating particles */}
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 rounded-full bg-primary/50"
                        style={{
                          top: `${15 + i * 25}%`,
                          left: i % 2 === 0 ? '-10px' : 'auto',
                          right: i % 2 !== 0 ? '-10px' : 'auto',
                        }}
                        animate={isInView ? { y: [0, -10, 0], opacity: [0.3, 0.8, 0.3] } : {}}
                        transition={{ duration: 2 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}
                      />
                    ))}
                  </motion.div>
                </motion.div>

                {/* Chat window */}
                <motion.div
                  className="bg-background border border-border rounded-2xl shadow-2xl overflow-hidden max-w-sm mx-auto relative z-10"
                  initial={{ y: 20, opacity: 0 }}
                  animate={isInView ? { y: 0, opacity: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {/* Chat header */}
                  <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border bg-muted/30">
                    <img src={auraMascot} alt="Aura AI" className="w-8 h-8 rounded-full object-cover border border-primary/20" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-foreground leading-tight">Aura AI</p>
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <p className="text-[9px] text-muted-foreground">Online now</p>
                      </div>
                    </div>
                    <span className="text-[9px] text-muted-foreground/50">just now</span>
                  </div>
                  {/* Chat messages with staggered entrance */}
                  <div className="p-3 sm:p-4 space-y-3">
                    <motion.div
                      className="flex justify-end"
                      initial={{ opacity: 0, x: 10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.6, duration: 0.4 }}
                    >
                      <div className="bg-foreground text-background rounded-2xl rounded-br-md px-3.5 py-2 text-xs max-w-[85%]">
                        Hey, I want to create an ecommerce store and run ads on Meta, but I don't know where to start. Can you help?
                      </div>
                    </motion.div>
                    <motion.div
                      className="flex gap-2"
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 1, duration: 0.4 }}
                    >
                      <img src={auraMascot} alt="Aura AI" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-foreground leading-relaxed">
                        <p>Of course. I'll guide you step by step.</p>
                        <p className="mt-1.5">First, tell me:</p>
                        <ul className="mt-1 ml-3 space-y-0.5 list-disc">
                          <li>What product or niche are you thinking about?</li>
                          <li>Do you already have a store, or are we starting from scratch?</li>
                        </ul>
                        <p className="mt-1.5">Once I know that, I'll help you build your store and launch your first ad campaign. 🚀</p>
                      </div>
                    </motion.div>
                  </div>
                  {/* Fake input bar */}
                  <div className="px-3 pb-3">
                    <motion.div
                      className="flex items-center gap-2 bg-muted/50 border border-border rounded-xl px-3.5 py-2.5"
                      animate={isInView ? { borderColor: ['hsl(var(--border))', 'hsl(var(--primary) / 0.3)', 'hsl(var(--border))'] } : {}}
                      transition={{ duration: 3, repeat: Infinity, delay: 2.5 }}
                    >
                      <span className="text-[11px] text-muted-foreground/50">Ask Aura anything...</span>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Social proof below chat */}
                <motion.div
                  className="text-center mt-3"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 2.2, duration: 0.5 }}
                >
                  <p className="text-[10px] text-muted-foreground/60">
                    💬 2,400+ questions answered this week
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
        {/* What You Can Build */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="text-center mb-8 mt-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 px-2">
            <TranslatedText>Faceless Online Businesses You Can Build</TranslatedText>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto px-2 leading-relaxed">
            <TranslatedText>Learn the exact systems used to build and automate these faceless online businesses.</TranslatedText>
          </p>
          <p className="text-sm font-semibold text-foreground/60 tracking-widest uppercase mt-3">
            Content · Traffic · Products · Automation
          </p>
        </motion.div>

        {/* Project Cards */}
        <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 mb-12">
          {projects.map((project, index) => {
            const colors = colorClasses[project.color as keyof typeof colorClasses];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
                className={`p-4 sm:p-5 rounded-xl ${colors.bg} border ${colors.border}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-background flex items-center justify-center shadow-sm flex-shrink-0">
                    <project.icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground">
                      <TranslatedText>{project.title}</TranslatedText>
                    </h3>
                    <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mt-1">
                      <TranslatedText>{project.description}</TranslatedText>
                    </p>
                  </div>
                </div>
                <p className={`text-[11px] font-medium mt-3 pl-14 ${colors.outcome}`}>
                  ↳ <TranslatedText>{project.outcome}</TranslatedText>
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* How You Learn To Build Them */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <h3 className="text-lg sm:text-xl font-bold text-foreground text-center mb-6">
            <TranslatedText>How You Learn to Build Them</TranslatedText>
          </h3>
          <div className="space-y-3">
            {[
              { emoji: "1️⃣", title: "Structured lessons", desc: "Short classes that show the system behind each project." },
              { emoji: "2️⃣", title: "Built-in AI tools", desc: "Generate scripts, captions, strategies, and visuals." },
              { emoji: "3️⃣", title: "Your AI personal teacher", desc: "Ask questions and get step-by-step help while building." },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
                <span className="text-lg flex-shrink-0 mt-0.5">{step.emoji}</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    <TranslatedText>{step.title}</TranslatedText>
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                    <TranslatedText>{step.desc}</TranslatedText>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Link to="/apply?plan=free" onClick={() => trackCTAClick('Start Building Free', 'Solution')}>
            <Button
              size="lg"
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 active:scale-[0.98] text-primary-foreground text-base font-semibold h-14 px-10 rounded-xl shadow-lg shadow-primary/25"
            >
              <TranslatedText>Start Building — It's Free</TranslatedText>
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default LightSolutionSection;
